import { useGeolocationStore } from '@/stores/geolocation.store';
import {
  notifyLocationDenied,
  notifyLocationUnavailable,
  type LocationAskReason,
} from '@/views/composables/hooks/useLocationHelp';
import { readonly, ref } from 'vue';

const LOCATION_FUNCTION_DONE_KEY = 'gam_location_function_done';

const reason = ref<LocationAskReason | null>(null);
let onAllowed: (() => void | Promise<void>) | null = null;

export const locationAskReason = readonly(reason);

export type { LocationAskReason };

/** This browser has finished a walk, a spot check-in, or a board move. */
export function rememberLocationFunction(): void {
  try {
    localStorage.setItem(LOCATION_FUNCTION_DONE_KEY, '1');
  } catch {
    // Private mode can refuse storage. The sentence stays until it can be saved.
  }
}

function hasRememberedLocationFunction(): boolean {
  try {
    return localStorage.getItem(LOCATION_FUNCTION_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Runs `next` when location is already allowed.
 * Until a walk, spot check-in, or board move has succeeded in this browser,
 * an undecided tap shows the sentence, and the browser dialog opens only after Allow.
 * After that success, an undecided tap opens the browser dialog directly.
 * A browser refusal opens the settings help for the same function.
 */
export function requestLocationFor(ask: LocationAskReason, next: () => void | Promise<void>): void {
  const geo = useGeolocationStore();
  if (geo.isLocationPermissionDenied) {
    notifyLocationDenied(ask);
    return;
  }
  if (geo.isLocationPermissionGranted) {
    void next();
    return;
  }
  if (hasRememberedLocationFunction()) {
    void openSystemLocationAsk(ask, next);
    return;
  }
  reason.value = ask;
  onAllowed = next;
}

async function openSystemLocationAsk(ask: LocationAskReason, next: () => void | Promise<void>): Promise<void> {
  const geo = useGeolocationStore();
  const coords = await geo.requestFreshPosition();
  if (geo.isLocationPermissionDenied) {
    notifyLocationDenied(ask);
    return;
  }
  if (!coords) {
    notifyLocationUnavailable(ask);
    return;
  }
  await next();
}

export async function allowLocationAsk(): Promise<void> {
  const ask = reason.value;
  const next = onAllowed;
  reason.value = null;
  onAllowed = null;
  if (!ask || !next) return;
  await openSystemLocationAsk(ask, next);
}

export function dismissLocationAsk(): void {
  reason.value = null;
  onAllowed = null;
}
